import { useNotificationStore } from '@/stores/notification.store';
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  cn,
} from '@ominiflow/ui';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AlertTriangle, Bell, CheckCircle2, Clock } from 'lucide-react';
import { useMemo, useState } from 'react';

export function SlaNotifications() {
  const [open, setOpen] = useState(false);

  // Subscribe ONLY to the raw notifications array (stable ref) â€” derive
  // filtered list with useMemo to avoid the Zustand "infinite loop"
  // pattern caused by selectors returning a new array reference on every call.
  const allNotifications = useNotificationStore((s) => s.notifications);

  const notifications = useMemo(
    () =>
      allNotifications.filter(
        (n) =>
          n.type === 'system_alert' &&
          (n.data?.type === 'SlaWarning' || n.data?.type === 'SlaBreached'),
      ),
    [allNotifications],
  );

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const clearAll = useNotificationStore((s) => s.clearAll);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground/70 hover:text-foreground"
          aria-label="Notificações SLA"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-4 min-w-4 p-0.5 flex items-center justify-center text-[10px] animate-in zoom-in"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 shadow-xl border-border/50">
        <div className="flex items-center justify-between p-4 bg-muted/30">
          <h3 className="text-sm font-semibold">Alertas de SLA</h3>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-[11px]">
              Lidos
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="text-[11px] text-destructive"
            >
              Limpar
            </Button>
          </div>
        </div>
        <Separator />
        <ScrollArea className="h-[350px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground gap-2 p-4">
              <CheckCircle2 className="h-8 w-8 opacity-20" />
              <p className="text-xs">Tudo em dia por aqui!</p>
            </div>
          ) : (
            <div className="flex flex-col">
              {notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => markAsRead(n.id)}
                  className={cn(
                    'flex gap-3 p-4 border-b border-border/40 hover:bg-accent/50 transition-colors cursor-pointer group relative text-left w-full',
                    !n.read && 'bg-primary/5',
                  )}
                >
                  <div
                    className={cn(
                      'mt-0.5 h-8 w-8 rounded-full flex items-center justify-center shrink-0',
                      n.data?.type === 'SlaBreached'
                        ? 'bg-red-500/10 text-red-500'
                        : 'bg-amber-500/10 text-amber-500',
                    )}
                  >
                    {n.data?.type === 'SlaBreached' ? (
                      <AlertTriangle className="h-4 w-4" />
                    ) : (
                      <Clock className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1 pr-6">
                    <p className="text-[13px] font-medium leading-tight">{n.title}</p>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                      {formatDistanceToNow(parseISO(n.createdAt), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
